'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';

import { useProfile } from '@/hooks/use-profile';
import { useAgeConfirmationStore } from '@/features/age-verification/store';
import {
  hasAgeConfirmation,
  hasGuestAgeConfirmationCookie,
} from '@/features/age-verification/utils';

interface AgeVerificationGuardProps {
  rating: string;
  isOriginal?: boolean;
  isAdult?: boolean;
  children: React.ReactNode;
}

export const AgeVerificationGuard = ({
  rating,
  isOriginal = false,
  isAdult = false,
  children,
}: AgeVerificationGuardProps) => {
  const router = useRouter();
  const { data: session } = useSession();
  const { userData } = useProfile();
  const { openDialog, isOpen, hasConfirmedInSession, hasLocalConfirmation } =
    useAgeConfirmationStore();
  const hasShownDialog = useRef(false);
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isRating18 = rating === '18';
  const isAuthenticated = !!session;
  const hasConfirmedAge =
    isAdult ||
    hasAgeConfirmation(userData) ||
    hasLocalConfirmation ||
    (!isAuthenticated && hasGuestAgeConfirmationCookie());
  const confirmedInSession = hasConfirmedInSession();

  useEffect(() => {
    if (isRating18 && !hasConfirmedAge && !confirmedInSession && !hasShownDialog.current) {
      hasShownDialog.current = true;
      openDialog(() => {
        // After confirmation, page remains accessible
      });
    }
  }, [isRating18, hasConfirmedAge, confirmedInSession, openDialog]);

  useEffect(() => {
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }

    if (
      isRating18 &&
      !hasConfirmedAge &&
      !confirmedInSession &&
      !isOpen &&
      hasShownDialog.current
    ) {
      redirectTimeoutRef.current = setTimeout(() => {
        const stillNeedsRedirect =
          !isAdult &&
          !hasAgeConfirmation(userData) &&
          !hasConfirmedInSession() &&
          !hasGuestAgeConfirmationCookie();

        if (stillNeedsRedirect) {
          const catalogPath = isOriginal ? '/originals' : '/fanfics';
          router.push(catalogPath);
        }
      }, 500);
    }

    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [
    isOpen,
    isRating18,
    hasConfirmedAge,
    confirmedInSession,
    isOriginal,
    router,
    userData,
    hasConfirmedInSession,
    isAdult,
  ]);

  return <>{children}</>;
};
